import {
  ArrowRight,
  BookOpen,
  CirclePlay,
  Download,
  ExternalLink,
  FileText,
  LifeBuoy,
  Scale,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { Badge, OfferBadge, OfferTierBadge } from "../components/ui";

const tutorialVideoUrl =
  "https://player.mediadelivery.net/embed/731884/6c7111cd-e3d5-445b-820f-ef916f9f7732?autoplay=false&loop=false&muted=false&preload=false&rememberPosition=false";

const helpSections = [
  {
    title: "A coordinated catalogue is not automatically a marketplace Series",
    body:
      "Paper Kingdom uses catalogue, collection, and family language to describe a visual and strategic relationship. Amazon KDP currently applies additional restrictions to low-content books, including Series and ISBN limitations. Verify the current rules for your exact book type before submission.",
  },
  {
    title: "AI-generated and AI-assisted work are different",
    body:
      "Record what the AI created and what you created. KDP currently asks publishers to disclose AI-generated text, images, or translations, while AI-assisted editing or refinement is treated differently. The publisher remains responsible for rights, accuracy, and quality.",
  },
  {
    title: "Metadata must tell the same truth as the product",
    body:
      "The title and subtitle in your listing should match the cover. Descriptions, keywords, categories, and claims must accurately describe the actual interior, format, audience, and contents. Do not use repeated keywords as a substitute for clear language.",
  },
  {
    title: "Every title needs meaningful substance",
    body:
      "Shared design is valuable; insufficiently differentiated content is not. Give each title a distinct reader job, module mix, examples, and purchase reason. Run the overlap audit before production.",
  },
  {
    title: "Rights verification is a production task",
    body:
      "Keep a source and licence record for text, fonts, images, templates, references, and generated outputs. Do not assume an AI output, stock asset, Canva element, or customer-supplied file is automatically cleared for the intended use.",
  },
  {
    title: "Physical geometry still decides print quality",
    body:
      "Use the official KDP cover calculator for the final trim size, paper, page count, bleed, spine width, and barcode-safe area. Review a printed proof for gutter, type size, ink, writing comfort, trim, and page flow.",
  },
  {
    title: "Search and advertising numbers require live evidence",
    body:
      "Paper Kingdom can structure keyword, category, listing, and campaign decisions, but it does not invent search volume, competition, ranking, sales, or advertising forecasts. Label assumptions and verify important decisions with current marketplace evidence.",
  },
  {
    title: "Reader funnels need clear permission",
    body:
      "Make QR destinations accurate, explain what a subscriber will receive, collect only necessary information, and include the privacy and unsubscribe controls required for your audience and jurisdiction. Never add readers to unrelated promotions without consent.",
  },
];

const helpQuickLinks = [
  {
    href: "#start-project",
    title: "Start a project",
    body: "Name the project and choose the right sales channel and format.",
  },
  {
    href: "#use-prompts",
    title: "Use the prompts",
    body: "Copy prepared instructions, work in your AI tool, and bring the result back.",
  },
  {
    href: "#make-files",
    title: "Make the files",
    body: "Plan the cover and pages, then create and check the real customer files.",
  },
  {
    href: "#prepare-to-sell",
    title: "Prepare to sell",
    body: "Create the listing, promotion copy, image plan, and publishing checklist.",
  },
  {
    href: "#download-backup",
    title: "Download & backup",
    body: "Understand autosave, written exports, and which files you must keep separately.",
  },
  {
    href: "#troubleshooting",
    title: "Troubleshooting",
    body: "Get direct answers to the most common workflow questions.",
  },
];

const writtenGuides = [
  {
    id: "start-project",
    number: "01",
    title: "Start a project with the right foundation",
    intro:
      "Paper Kingdom carries these first choices into later prompts, file instructions, and selling tools, so keep them short and accurate.",
    steps: [
      {
        title: "Choose New project and give it a useful name",
        body:
          "The project name is for your own dashboard. Use something you will recognize later; it can be changed with Rename project.",
      },
      {
        title: "Choose one sales channel",
        body:
          "Select Amazon KDP, Etsy, Shopify / direct, or Creative Market. Start a separate project for another channel so its files and listing requirements stay clear.",
      },
      {
        title: "Choose the product format",
        body:
          "The available formats adjust to the channel. Pick the closest real result, such as a guided journal, workbook, planner, activity book, low-content paperback, or printable PDF.",
      },
      {
        title: "Describe the reader and the job",
        body:
          "Use plain language: who is it for, and what should it help them do? A short, specific answer is more useful than a long sales pitch.",
      },
    ],
    note:
      "Beginner example: A balcony gardening planner for first-time growers who need to remember watering and weekly tasks.",
  },
  {
    id: "use-prompts",
    number: "02",
    title: "Use prompts without losing the useful work",
    intro:
      "Paper Kingdom prepares detailed instructions; ChatGPT or another suitable AI tool creates the answer. The review step keeps only the work you have checked.",
    steps: [
      {
        title: "Begin with Recommended prompts",
        body:
          "These prompts cover the most useful first-book tasks in a sensible order. Use the Advanced prompt library only when you need a more specific task.",
      },
      {
        title: "Add a short personal request when needed",
        body:
          "Tell the prompt what should change for this result. Paper Kingdom combines that request with the project facts and safety boundaries.",
      },
      {
        title: "Choose Copy prompt",
        body:
          "Paste the complete copied prompt into ChatGPT or another AI tool. Do not send only the short personal request, because that omits the prepared context.",
      },
      {
        title: "Paste the useful answer back and review it",
        body:
          "Read the result, correct weak or inaccurate parts, paste the complete useful answer into Paper Kingdom, then choose Review and use only when you are satisfied.",
      },
    ],
    note:
      "Image prompts are instructions for an image-capable tool. Keep the downloaded image file yourself; reviewing an answer does not upload that file to Paper Kingdom.",
  },
  {
    id: "make-files",
    number: "03",
    title: "Turn the plan into real customer files",
    intro:
      "The Make the book area separates the work into Cover, Inside pages, and Create files so a plan is never mistaken for a finished product.",
    steps: [
      {
        title: "Set the cover direction",
        body:
          "Choose the visual style, colors, and cover rules, then create the artwork in the design or AI tool you prefer. Confirm that every font and image is licensed for the intended use.",
      },
      {
        title: "Build the inside-page plan",
        body:
          "Review the proposed page types, page order, trim size, and target page count. Adjust them until the reader has a complete and usable experience.",
      },
      {
        title: "Use Create files for production instructions",
        body:
          "For KDP, use the current cover calculator and create the actual print-ready interior and cover PDFs. For digital channels, create the exact customer download and preview files shown in the project.",
      },
      {
        title: "Check evidence before marking work complete",
        body:
          "Open the real files and check size, page count, margins, text, images, links, naming, and customer usability. A prompt response, code sample, or design description is not the finished file.",
      },
    ],
    note:
      "For printed books, use the official marketplace calculator and order a proof when available. Paper Kingdom cannot see or approve a file kept in another tool.",
  },
  {
    id: "prepare-to-sell",
    number: "04",
    title: "Create the selling kit and publishing checklist",
    intro:
      "The Prepare to sell area turns your finished product facts into channel-specific listing and launch material.",
    steps: [
      {
        title: "Create the complete selling kit",
        body:
          "Choose Copy prompt, run it in your AI tool, and paste the complete answer into Paste the completed selling-kit answer.",
      },
      {
        title: "Import the answer with Fill my selling kit",
        body:
          "The answer must be plain, valid JSON with no introduction or code fences. If import fails, ask the AI tool to return only the requested JSON, or open the manual fields and add the copy yourself.",
      },
      {
        title: "Review every claim before using it",
        body:
          "Check the title, description, keywords or tags, promotion copy, and image plan against the real product. Remove invented statistics, guarantees, reviews, or features.",
      },
      {
        title: "Finish the publishing checklist",
        body:
          "Confirm the customer file, listing, previews, rights, disclosures, price, delivery, and live platform requirements. Optional tools are optional; use only the ones that serve this project.",
      },
    ],
    note:
      "Marketplace rules change. Use the official links later on this page immediately before publishing rather than treating old notes as current policy.",
  },
  {
    id: "download-backup",
    number: "05",
    title: "Save, rename, download, and back up safely",
    intro:
      "Paper Kingdom preserves the project record, while the real PDFs and images created in other tools remain your responsibility.",
    steps: [
      {
        title: "Wait for the cloud save to finish",
        body:
          "After an edit, wait for Saving to cloud… to change to Saved to cloud before closing the page or reloading. There is no separate Save button.",
      },
      {
        title: "Rename without starting over",
        body:
          "Use the pencil action on the dashboard or beside the name inside a project. Renaming changes the dashboard label without replacing the project work.",
      },
      {
        title: "Use Download everything for the written handoff",
        body:
          "This export gathers the checklist, prompts, reviewed answers, book-making notes, and selling notes into one readable record.",
      },
      {
        title: "Keep actual assets in a separate project folder",
        body:
          "The written handoff does not contain the final interior PDF, cover PDF, downloaded artwork, marketplace template, or other files created outside Paper Kingdom. Back up those files separately.",
      },
    ],
    note:
      "A complete backup has both parts: the Paper Kingdom written record and a clearly named folder containing every real deliverable and source file.",
  },
];

const troubleshootingItems = [
  {
    question: "Why did Paper Kingdom give me a prompt instead of an answer?",
    answer:
      "That is the intended workflow. Paper Kingdom prepares project-aware instructions; you run them in ChatGPT or another suitable AI tool, then paste useful results back for review and later steps.",
  },
  {
    question: "Why will my selling-kit answer not import?",
    answer:
      "The importer needs the exact JSON requested by the prompt, with no opening sentence, closing note, or markdown code fence. Ask the AI tool to return only valid JSON, or enter the information through the manual fields.",
  },
  {
    question: "Where are the cover, image, and PDF files I created?",
    answer:
      "Files made or downloaded in another AI, design, or document tool are not uploaded automatically. Keep them in your own project folder; Paper Kingdom stores the planning and written project record.",
  },
  {
    question: "Do I need to complete every advanced or optional tool?",
    answer:
      "No. Complete the core path first: project brief, product idea, useful prompts, real customer files, truthful selling kit, and publishing checklist. Add advanced tools only when they solve a real need.",
  },
  {
    question: "How do I know my latest changes were saved?",
    answer:
      "Watch the status in the application header. Wait until Saving to cloud… becomes Saved to cloud before you reload, close the tab, or move to another device.",
  },
  {
    question: "Does Paper Kingdom guarantee approval or sales?",
    answer:
      "No. You are responsible for the finished files, rights, disclosures, listing accuracy, current platform requirements, and customer experience. Marketplace acceptance and commercial results are never guaranteed.",
  },
];

export function HelpPage() {
  return (
    <main className="reference-page reference-page--help">
      <header className="help-header">
        <Badge tone="green"><LifeBuoy size={14} /> Help &amp; tutorials</Badge>
        <h1>Learn Paper Kingdom your way</h1>
        <p>
          Watch the complete walkthrough, follow the written guides, or jump straight to the
          feature you need. Everything here follows the same workflow and wording you see inside
          the application.
        </p>
      </header>

      <section className="help-video-section" aria-labelledby="tutorial-video-title">
        <div className="help-video-copy">
          <Badge tone="gold"><CirclePlay size={14} /> Video tutorial</Badge>
          <h2 id="tutorial-video-title">Watch the complete Paper Kingdom walkthrough</h2>
          <p>
            See the whole journey in context: starting a project, using prepared prompts, planning
            the product, creating the selling material, and taking your work with you.
          </p>
          <ul>
            <li><Sparkles size={15} /> Follow the real in-app workflow</li>
            <li><BookOpen size={15} /> Pause and use the written guide beside it</li>
            <li><Download size={15} /> Understand what is saved and downloaded</li>
          </ul>
        </div>
        <div className="help-video-frame">
          <iframe
            src={tutorialVideoUrl}
            title="Paper Kingdom complete tutorial"
            allow="accelerometer; gyroscope; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </section>

      <nav className="help-quick-links" aria-label="Help topics">
        <header>
          <Badge tone="clay"><FileText size={14} /> Written documentation</Badge>
          <h2>Choose what you need</h2>
          <p>Each link opens the exact part of the guide for that stage of the application.</p>
        </header>
        <div>
          {helpQuickLinks.map((link) => (
            <a href={link.href} key={link.href}>
              <span><strong>{link.title}</strong><small>{link.body}</small></span>
              <ArrowRight aria-hidden="true" size={17} />
            </a>
          ))}
        </div>
      </nav>

      <section className="help-written-guides" aria-labelledby="written-guides-title">
        <header className="help-section-header">
          <Badge tone="green"><BookOpen size={14} /> Step-by-step guides</Badge>
          <h2 id="written-guides-title">Use every core feature with confidence</h2>
          <p>
            Start at the top for your first project. After that, return to the section that matches
            the tab you are using in Studio.
          </p>
        </header>
        <div className="help-guide-list">
          {writtenGuides.map((guide) => (
            <article className="help-guide" id={guide.id} key={guide.id}>
              <header>
                <span>{guide.number}</span>
                <div>
                  <h3>{guide.title}</h3>
                  <p>{guide.intro}</p>
                </div>
              </header>
              <ol>
                {guide.steps.map((step) => (
                  <li key={step.title}>
                    <strong>{step.title}</strong>
                    <p>{step.body}</p>
                  </li>
                ))}
              </ol>
              <p className="help-guide__note"><Sparkles aria-hidden="true" size={16} /> {guide.note}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="help-troubleshooting" id="troubleshooting" aria-labelledby="troubleshooting-title">
        <header className="help-section-header">
          <Badge tone="clay"><LifeBuoy size={14} /> Troubleshooting</Badge>
          <h2 id="troubleshooting-title">Quick answers when something feels unclear</h2>
          <p>Open a question to see the answer. These cover the most common first-project misunderstandings.</p>
        </header>
        <div>
          {troubleshootingItems.map((item) => (
            <details key={item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="plain-glossary help-glossary">
        <h2>Words you may see</h2>
        <dl>
          <div><dt>KDP</dt><dd>Amazon's system for publishing books and print-on-demand paperbacks.</dd></div>
          <div><dt>Print on demand</dt><dd>A physical copy is manufactured after a customer orders it.</dd></div>
          <div><dt>Inside pages</dt><dd>The actual pages the reader receives, writes in, or prints.</dd></div>
          <div><dt>Metadata</dt><dd>The title, description, search phrases, categories, and other product-page information.</dd></div>
          <div><dt>Preflight</dt><dd>A final evidence-based check before a file is uploaded or sold.</dd></div>
          <div><dt>Bleed</dt><dd>Artwork that extends beyond the cut edge so no unwanted white line appears after trimming.</dd></div>
        </dl>
      </section>

      <header className="help-section-header">
        <Badge tone="clay"><ShieldCheck size={14} /> Before publishing</Badge>
        <h2>Eight checks that protect the customer and your work</h2>
        <p>Only mark a check complete when the final file or current marketplace evidence supports it.</p>
      </header>
      <section className="reference-layout">
        <article className="reference-main">
          <div className="guidance-list">
            {helpSections.map((section, index) => (
              <section key={section.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div><h2>{section.title}</h2><p>{section.body}</p></div>
              </section>
            ))}
          </div>
        </article>
        <aside className="reference-sidebar">
          <div className="warning-card">
            <TriangleAlert size={21} />
            <h2>No outcome promises</h2>
            <p>
              Do not promise marketplace acceptance, search ranking, sales, reviews, profit, or
              income. Treat every result as uncertain and every platform decision as the
              platform’s own.
            </p>
          </div>
          <div className="resource-card">
            <h2>Verify at the source</h2>
            <a href="https://kdp.amazon.com/en_US/help/topic/GGE5T76TWKA85DJM" target="_blank" rel="noreferrer">
              KDP low-content guidance <ExternalLink size={14} />
            </a>
            <a href="https://kdp.amazon.com/en_US/help/topic/G200672390" target="_blank" rel="noreferrer">
              KDP content and AI guidance <ExternalLink size={14} />
            </a>
            <a href="https://kdp.amazon.com/en_US/help/topic/G201097560" target="_blank" rel="noreferrer">
              KDP metadata guidance <ExternalLink size={14} />
            </a>
            <a href="https://kdp.amazon.com/cover-calculator" target="_blank" rel="noreferrer">
              KDP cover calculator <ExternalLink size={14} />
            </a>
            <a href="https://www.canva.com/help/using-canva-to-create-products-for-sale/" target="_blank" rel="noreferrer">
              Canva commercial-use guidance <ExternalLink size={14} />
            </a>
            <a href="#licences">
              Paper Kingdom licence summaries
            </a>
            <small>Policies change. Review these sources again immediately before launch.</small>
          </div>
        </aside>
      </section>
      <section className="help-licence-section" id="licences">
        <header>
          <Badge tone="clay"><Scale size={14} /> Licence summaries</Badge>
          <h2>Know what each purchase lets you create</h2>
          <p>Every upgrade adds content or permissions. It never silently changes another offer’s terms.</p>
        </header>
        <div className="help-license-grid">
          <article>
            <OfferTierBadge sku="paper-kingdom-unlimited" />
            <strong>Core tools and more examples</strong>
            <p>Create and sell your own original finished publications. Do not redistribute the app, prompts, or source libraries.</p>
          </article>
          <article>
            <OfferTierBadge sku="paper-kingdom-dfy" />
            <strong>Ready-made book plans</strong>
            <p>Adapt the supplied concepts into original publications after completing live title, rights, market, and quality checks.</p>
          </article>
          <article>
            <OfferTierBadge sku="paper-kingdom-production" />
            <strong>Book-making tools</strong>
            <p>Use the cover, interior, specification, and preflight systems for your own finished books; verify every asset licence and print proof.</p>
          </article>
          <article>
            <OfferTierBadge sku="paper-kingdom-launch-growth" />
            <strong>Selling tools</strong>
            <p>Use the listing and audience-growth systems for your books without fabricating demand, performance, reviews, or consent.</p>
          </article>
          <article>
            <OfferTierBadge sku="paper-kingdom-reseller" />
            <strong>Reseller</strong>
            <p>Resell the branded customer product under its existing terms. No pass-through resale, private-label, or source-code rights.</p>
          </article>
        </div>
      </section>
    </main>
  );
}

export function LicensesPage() {
  return (
    <main className="reference-page">
      <section className="page-hero">
        <div>
          <Badge tone="clay"><Scale size={14} /> Licence overview</Badge>
          <h1>Use the product widely. Keep the rights clear.</h1>
          <p>
            Each Paper Kingdom offer adds specific content or commercial permissions. Access is
            additive; no upgrade silently removes a restriction from another offer.
          </p>
        </div>
      </section>
      <section className="content-section license-grid">
        <article>
          <OfferBadge sku="paper-kingdom-unlimited" />
          <h2>Publishing-use licence</h2>
          <p>
            Create unlimited projects and use the included prompts, frameworks, and modules to
            develop your own finished publications. You may sell those original finished books.
          </p>
          <h3>You may not</h3>
          <ul>
            <li>redistribute the prompt library, application, or source files;</li>
            <li>sell the included content as a competing prompt or template pack;</li>
            <li>claim exclusive ownership of generic methods or ideas;</li>
            <li>pass through resale or white-label rights.</li>
          </ul>
        </article>
        <article>
          <OfferBadge sku="paper-kingdom-dfy" />
          <h2>Blueprint-use licence</h2>
          <p>
            Adapt the catalogue blueprints, title concepts, worksheets, and layout specifications
            into original publications for your own business.
          </p>
          <h3>You remain responsible for</h3>
          <ul>
            <li>live title, trademark, category, and market checks;</li>
            <li>substantive differentiation and final quality;</li>
            <li>all font, image, and design-tool asset licences;</li>
            <li>current AI disclosure and marketplace compliance.</li>
          </ul>
        </article>
        <article>
          <OfferBadge sku="paper-kingdom-production" />
          <h2>Production-use licence</h2>
          <p>
            Adapt the cover-family systems, interior masters, production records, prompts, and
            operational resources into original finished publications for your own business.
          </p>
          <h3>You remain responsible for</h3>
          <ul>
            <li>confirming print geometry with current official calculators;</li>
            <li>licensing every font, image, template, and source asset;</li>
            <li>checking printed proofs and resolving title-level preflight issues;</li>
            <li>keeping source, rights, revision, and approval records accurate.</li>
          </ul>
        </article>
        <article>
          <OfferBadge sku="paper-kingdom-launch-growth" />
          <h2>Marketing-use licence</h2>
          <p>
            Adapt the listing, A+ Content, launch, lead-magnet, welcome, and cross-sell systems to
            promote your own original publications and catalogue.
          </p>
          <h3>You may not</h3>
          <ul>
            <li>fabricate keyword volume, rankings, sales, reviews, or advertising performance;</li>
            <li>make misleading product, scarcity, endorsement, or outcome claims;</li>
            <li>send email without appropriate consent and unsubscribe controls;</li>
            <li>redistribute the source libraries as a competing template product.</li>
          </ul>
        </article>
        <article>
          <OfferBadge sku="paper-kingdom-reseller" />
          <h2>Branded resale rights</h2>
          <p>
            Resell the branded Paper Kingdom customer product and use the supplied delivery and
            campaign materials. You handle customer support, sales claims, refunds, taxes, and
            secure delivery.
          </p>
          <h3>Excluded</h3>
          <ul>
            <li>pass-through resale, master-resale, or private-label rights;</li>
            <li>source-code rights or removal of ownership notices;</li>
            <li>income, ranking, sales, acceptance, or compliance guarantees;</li>
            <li>redistribution of third-party assets beyond their own licence.</li>
          </ul>
        </article>
      </section>
      <p className="legal-note">
        This in-product summary is operational guidance, not legal advice. The final customer
        agreement should be reviewed for the governing jurisdiction before launch.
      </p>
    </main>
  );
}
